/**
 * Recovery from a chunk that no longer exists.
 *
 * Every rolling deploy replaces the container's `dist` output, and each build names its
 * chunks with a fresh content hash. A tab loaded before the rollout is therefore holding a document
 * and a webpack runtime that point at URLs the new container does not serve. The next lazy
 * import fails — and in this shell every route is a lazy import, so that is the
 * first thing the page does — the bundler rejects with a ChunkLoadError, React unwinds to the
 * boundary, and the user is left at "Something Went Wrong" until they reload by hand.
 *
 * Reloading is the whole fix: it fetches the new HTML and with it the new chunk names. So the
 * boundary does it rather than asking. Two constraints keep that from being worse than the
 * dead end it replaces:
 *
 *   1. Only OUR OWN chunks. A remote App's container is served from another origin, and when
 *      a remote host is down a reload changes nothing — the tab would spin against a failure
 *      it cannot fix. webpack records the failing URL on the error, so this is checked rather
 *      than inferred. The cost of that conservatism is that a remote's own stale chunks are
 *      not auto-recovered, which is the right trade: a reload there is a guess, here it is a
 *      certainty.
 *   2. At most once per build. If the new build is itself broken, the second failure falls
 *      through to the boundary — a card the user can read beats a reload loop. The marker is
 *      keyed by buildId, so the next deploy gets its own single attempt.
 */

import { config } from '@/config/runtime';

const RELOAD_MARKER_PREFIX = 'shell:chunk-reload:';

/**
 * The build this document came from. `__NEXT_DATA__` is the authority — it is what the chunk
 * names are derived from and it changed on every build.
 *
 * That id is gone with Next — `__NEXT_DATA__` no longer exists — so the commit SHA the
 * image was built from is the identity now, and it is the better one: stable across a
 * rebuild of the same source, and meaningful to a human reading an incident report.
 */
const currentBuildId = (): string => config.buildId;

/**
 * The URL webpack failed to load. It is set as `request` on the error (from the script tag's
 * src); the message is parsed only for runtimes that carry the URL there and nowhere else.
 */
const failedChunkUrl = (error: Error): string | null => {
  const request = (error as Error & { request?: unknown }).request;
  if (typeof request === 'string' && request) return request;
  // "Loading chunk 42 failed.\n(error: https://host/_next/static/chunks/42.abc.js)"
  const match = /\((?:[a-z]+):\s*(https?:\/\/[^)\s]+)\)/i.exec(error.message || '');
  return match ? match[1] : null;
};

/**
 * True only for a failure to load a chunk of THIS origin — the signature of a document that
 * outlived its build.
 *
 * Deliberately returns false when the failing URL cannot be determined: an unattributable
 * load failure is not proof the build moved, and reloading on it risks spinning on something
 * a reload cannot mend.
 */
export function isStaleChunkError(error: unknown): boolean {
  if (typeof window === 'undefined') return false;
  if (!(error instanceof Error)) return false;

  const looksLikeChunkLoad =
    error.name === 'ChunkLoadError' ||
    /Loading (?:CSS )?chunk \S+ failed/i.test(error.message || '');
  if (!looksLikeChunkLoad) return false;

  const url = failedChunkUrl(error);
  if (!url) return false;

  try {
    return new URL(url, window.location.href).origin === window.location.origin;
  } catch {
    return false;
  }
}

/**
 * Reload once to pick up the current build. Returns whether the reload was started, so the
 * caller can fall back to showing the error when it was not.
 */
export function recoverFromStaleChunk(error: unknown): boolean {
  if (!isStaleChunkError(error)) return false;

  const marker = RELOAD_MARKER_PREFIX + currentBuildId();
  try {
    if (window.sessionStorage.getItem(marker)) return false;
    window.sessionStorage.setItem(marker, '1');
  } catch {
    // Safari in private browsing — and any browser with site data blocked — throws on access
    // instead of returning null. With no way to record the attempt there is no way to stop a
    // second one, so decline: the boundary renders and its Reload Page button is one click.
    return false;
  }

  // After the marker, and no delay needed: reload() fires `pagehide`, which is where the
  // client logger beacons its buffer, so the report naming this reload still ships.
  window.location.reload();
  return true;
}

/** Exported for the boundary's log line — which build the dead document belonged to. */
export const staleChunkContext = (error: unknown): Record<string, unknown> => ({
  build_id: currentBuildId(),
  chunk_url: error instanceof Error ? failedChunkUrl(error) : null,
});
